import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { conditions } from '@character-creator/data';
import {
  useRulesetSpells,
  useRulesetClasses,
  useRulesetRaces,
  useRulesetFeats,
} from '../hooks/useRulesetData';

interface TermDefinition {
  name: string;
  id: string;
  path: string;
}

interface LinkedTextProps {
  children: string;
  className?: string;
  /** Pages to exclude from linking (to avoid self-links) */
  excludePages?: Array<'conditions' | 'spells' | 'classes' | 'races' | 'feats'>;
}

/**
 * LinkedText component that automatically converts recognized game terms
 * into clickable links to their reference pages.
 *
 * Recognizes: conditions, spells, classes, races, feats
 */
export function LinkedText({ children, className, excludePages = [] }: LinkedTextProps) {
  const spells = useRulesetSpells();
  const classes = useRulesetClasses();
  const races = useRulesetRaces();
  const feats = useRulesetFeats();

  // Build term dictionary, sorted by name length (longest first) to match
  // longer terms before shorter ones (e.g., "Hold Person" before "Hold")
  const terms = useMemo(() => {
    const allTerms: TermDefinition[] = [];

    // Add conditions (static, not ruleset-dependent)
    if (!excludePages.includes('conditions')) {
      conditions.forEach((c) => {
        allTerms.push({ name: c.name, id: c.id, path: '/conditions' });
      });
    }

    // Add spells
    if (!excludePages.includes('spells')) {
      spells.forEach((s) => {
        allTerms.push({ name: s.name, id: s.id, path: '/spells' });
      });
    }

    // Add classes
    if (!excludePages.includes('classes')) {
      classes.forEach((c) => {
        allTerms.push({ name: c.name, id: c.id, path: '/classes' });
      });
    }

    // Add races
    if (!excludePages.includes('races')) {
      races.forEach((r) => {
        allTerms.push({ name: r.name, id: r.id, path: '/races' });
      });
    }

    // Add feats
    if (!excludePages.includes('feats')) {
      feats.forEach((f) => {
        allTerms.push({ name: f.name, id: f.id, path: '/feats' });
      });
    }

    // Sort by name length descending so longer matches are found first
    return allTerms.sort((a, b) => b.name.length - a.name.length);
  }, [spells, classes, races, feats, excludePages]);

  // Build regex pattern from all term names
  const pattern = useMemo(() => {
    if (terms.length === 0) return null;

    // Escape special regex characters in term names
    const escapedNames = terms.map((t) =>
      t.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    );

    // Match whole words only, case-insensitive
    return new RegExp(`\\b(${escapedNames.join('|')})\\b`, 'gi');
  }, [terms]);

  // Parse text and create linked segments
  const linkedContent = useMemo((): ReactNode => {
    if (!pattern || !children) return children;

    const segments: ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let keyIndex = 0;

    // Create a fresh regex to avoid state issues with lastIndex
    const freshPattern = new RegExp(pattern.source, pattern.flags);

    while ((match = freshPattern.exec(children)) !== null) {
      const matchedText = match[0];
      const matchIndex = match.index;

      // Add text before match
      if (matchIndex > lastIndex) {
        segments.push(children.slice(lastIndex, matchIndex));
      }

      // Find the term definition (case-insensitive)
      const term = terms.find(
        (t) => t.name.toLowerCase() === matchedText.toLowerCase()
      );

      if (term) {
        segments.push(
          <Link
            key={`link-${keyIndex++}`}
            to={`${term.path}#${term.id}`}
            className="text-accent-gold hover:underline"
          >
            {matchedText}
          </Link>
        );
      } else {
        segments.push(matchedText);
      }

      lastIndex = matchIndex + matchedText.length;
    }

    // Add remaining text after last match
    if (lastIndex < children.length) {
      segments.push(children.slice(lastIndex));
    }

    return segments.length > 0 ? segments : children;
  }, [children, pattern, terms]);

  return <span className={className}>{linkedContent}</span>;
}
